"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ModalTypeEnum, useModal } from "@/components/providers/modal-provider/ModalProvider";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type Variant = "default" | "primary";
type Props = {
  label?: string;
  sublabel?: string;
  placeholder?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  onClear?: () => void;
  variant?: Variant;
  className?: string;
  disabled?: boolean;
  children: ReactNode | ((args: {
    close: () => void;
  }) => ReactNode);
};
export function DropdownPickerButton(t0) {
  const $ = _c(46);
  const {
    label,
    sublabel,
    placeholder,
    dialogTitle,
    dialogDescription,
    onClear,
    variant: t1,
    className,
    disabled,
    children
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const isMobile = useIsMobile();
  const {
    openModal,
    closeModal
  } = useModal();
  const [open, setOpen] = useState(false);
  let t2;
  if ($[0] !== closeModal || $[1] !== isMobile) {
    t2 = () => {
      setOpen(false);
      if (isMobile) {
        closeModal();
      }
    };
    $[0] = closeModal;
    $[1] = isMobile;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const close = t2;
  let t3;
  if ($[3] !== children || $[4] !== close) {
    t3 = () => typeof children === "function" ? children({
      close
    }) : children;
    $[3] = children;
    $[4] = close;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const renderChildren = t3;
  const t4 = variant === "primary" ? "default" : "simple";
  let t5;
  if ($[6] !== dialogDescription || $[7] !== dialogTitle || $[8] !== isMobile || $[9] !== openModal || $[10] !== renderChildren) {
    t5 = () => {
      if (isMobile) {
        openModal(ModalTypeEnum.GENERIC_SHEET, {
          title: dialogTitle,
          description: dialogDescription,
          side: "bottom",
          className: "w-full",
          childrenClassName: "px-4 pb-6",
          children: renderChildren()
        });
      }
    };
    $[6] = dialogDescription;
    $[7] = dialogTitle;
    $[8] = isMobile;
    $[9] = openModal;
    $[10] = renderChildren;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== className) {
    t6 = cn("flex w-full h-auto justify-between py-2 px-3 rounded-md text-sm font-normal", className);
    $[12] = className;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== label || $[15] !== placeholder || $[16] !== sublabel || $[17] !== variant) {
    t7 = <span className="min-w-0 flex-1 text-left">{label ? <span className="flex flex-col"><span className={cn("font-medium truncate capitalize", variant === "primary" ? "text-white" : "text-zinc-800")}>{label}</span>{sublabel && <span className={cn("text-xs truncate capitalize", variant === "primary" ? "text-white/80" : "text-zinc-500")}>{sublabel}</span>}</span> : <span className={cn(variant === "primary" ? "text-white/90" : "text-zinc-500")}>{placeholder}</span>}</span>;
    $[14] = label;
    $[15] = placeholder;
    $[16] = sublabel;
    $[17] = variant;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== label || $[20] !== onClear || $[21] !== variant) {
    t8 = label && onClear ? <span role="button" tabIndex={0} aria-label="Clear selection" onPointerDown={_temp} onClick={e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      onClear();
    }} onKeyDown={e_1 => {
      if (e_1.key === "Enter" || e_1.key === " ") {
        e_1.preventDefault();
        e_1.stopPropagation();
        onClear();
      }
    }} className={cn("flex items-center justify-center w-6 h-6 rounded-md shrink-0 cursor-pointer", variant === "primary" ? "text-white/90 hover:bg-white/20" : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700")}><X className="w-4 h-4" /></span> : <ChevronDown className={cn(variant === "primary" ? "text-white/90" : "text-zinc-500")} />;
    $[19] = label;
    $[20] = onClear;
    $[21] = variant;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== disabled || $[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <Button type="button" variant={t4} disabled={disabled} onClick={t5} className={t6}>{t7}{t8}</Button>;
    $[23] = disabled;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const trigger = t9;
  if (isMobile) {
    return trigger;
  }
  let t10;
  if ($[30] !== trigger) {
    t10 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="DropdownPickerButton.tsx">{trigger}</PopoverTrigger>;
    $[30] = trigger;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== dialogDescription || $[33] !== dialogTitle) {
    t11 = (dialogTitle || dialogDescription) && <div className="mb-3">{dialogTitle && <p className="font-semibold text-zinc-800">{dialogTitle}</p>}{dialogDescription && <p className="text-xs text-zinc-500">{dialogDescription}</p>}</div>;
    $[32] = dialogDescription;
    $[33] = dialogTitle;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== renderChildren) {
    t12 = renderChildren();
    $[35] = renderChildren;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12) {
    t13 = <div className="max-h-[60vh] overflow-y-auto px-2 py-1" onWheel={_temp2}>{t12}</div>;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t11 || $[40] !== t13) {
    t14 = <PopoverContent align="end" sideOffset={6} className="w-fit max-w-[calc(100vw-2rem)] p-3" data-sentry-element="PopoverContent" data-sentry-source-file="DropdownPickerButton.tsx">{t11}{t13}</PopoverContent>;
    $[39] = t11;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== open || $[43] !== t10 || $[44] !== t14) {
    t15 = <Popover open={open} onOpenChange={setOpen} preventClickThrough={false} data-sentry-element="Popover" data-sentry-component="DropdownPickerButton" data-sentry-source-file="DropdownPickerButton.tsx">{t10}{t14}</Popover>;
    $[42] = open;
    $[43] = t10;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
function _temp2(e_2) {
  e_2.currentTarget.scrollTop = e_2.currentTarget.scrollTop + e_2.deltaY;
  e_2.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}